import React, { useCallback } from 'react';

import { CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import _ from 'lodash';
import SearchResultCell from 'src/admin/screens/UserProfileScreen/components/SearchResultCell';

import SavedSheetCell from '../../../../florence/components/SavedSheetsCell/SavedSheetCell';
import { useGetAdminSearchResultsByUserQuery } from '../../../apis/AdminApi';
import { useGetSavedNotesQuery } from '../../../apis/CommonApi';
import { SaveSheet, SheetStatus } from '../../../types/SaveSheet';
import { SelectorType } from '../../../types/SelectorType';
import { CurrentUser, User } from '../../../types/User';
import PaginationControl from '../../PaginationControlLight/PaginationControlLight';

type Props = {
  user: CurrentUser | User;
  type: 'admin' | 'florence';
  statusFilter?: SheetStatus;
  clinicNotes: boolean;
  searchTerms?: string;
  openDeleteSheetContainer: (savedSheet: SaveSheet) => void;
  openRenameSheetContainer: (savedSheet: SaveSheet) => void;
  renderLoadingSpinner: () => JSX.Element;
  renderNoRecordsText: (type: SelectorType) => JSX.Element;
  renderNoSubscriptionText: (type: SelectorType) => JSX.Element;
};

const getPreservedPageState = (
  appType: 'admin' | 'florence',
  clinicNotes: boolean
) => {
  if (appType === 'admin') return 1;

  const lastPage = localStorage.getItem(
    `last${clinicNotes ? 'ClinicNotes' : 'OPNotes'}PageSelected`
  );

  return lastPage ? parseInt(lastPage) : 1;
};

const SavedSheetsContainer: React.FC<Props> = ({
  type,
  user,
  statusFilter,
  clinicNotes = false,
  searchTerms = '',
  openDeleteSheetContainer,
  openRenameSheetContainer,
  renderLoadingSpinner,
  renderNoRecordsText,
  renderNoSubscriptionText,
}) => {
  const [sheetPage, setSheetPage] = React.useState(
    getPreservedPageState(type, clinicNotes)
  );
  const [localSearchTerms, setLocalSearchTerms] =
    React.useState<string>(searchTerms);

  const { data: savedSheetsResponse, isFetching: savedSheetsIsLoading } =
    useGetSavedNotesQuery(
      {
        limit: 10,
        page: sheetPage,
        status: statusFilter,
        clinicNotes: clinicNotes,
        searchQuery: localSearchTerms,
      },
      {
        skip: type === 'admin',
        refetchOnMountOrArgChange: true,
      }
    );
  const savedSheets = savedSheetsResponse?.savedSheets;

  const { data: adminSearchResults, isFetching: adminSearchResultsIsLoading } =
    useGetAdminSearchResultsByUserQuery(
      { id: user.id.toString(), clinicNotes: clinicNotes },
      {
        skip: type === 'florence',
        refetchOnFocus: true,
        refetchOnMountOrArgChange: 60,
      }
    );

  const changePage = useCallback(
    (page: number) => {
      setSheetPage(page);
      localStorage.setItem(
        `last${clinicNotes ? 'ClinicNotes' : 'OPNotes'}PageSelected`,
        page.toString()
      );
    },
    [setSheetPage, clinicNotes]
  );

  React.useEffect(() => {
    setLocalSearchTerms(searchTerms);
    if (searchTerms !== localSearchTerms) {
      changePage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerms, changePage]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [sheetPage]);

  if (savedSheetsIsLoading || adminSearchResultsIsLoading || !user) {
    return renderLoadingSpinner();
  }

  if (_.isEmpty(type === 'florence' ? savedSheets : adminSearchResults)) {
    return renderNoRecordsText(clinicNotes ? 'ClinicNotes' : 'OPNotes');
  }

  if (
    type === 'florence' &&
    !user.activeSubscriptionType &&
    !user.organizationId
  ) {
    return renderNoSubscriptionText(clinicNotes ? 'ClinicNotes' : 'OPNotes');
  }

  const shouldReturnFewer = () => {
    return (
      user.activeSubscriptionType === 'mobile' ||
      user.activeSubscriptionType === 'trial'
    );
  };

  const results = shouldReturnFewer()
    ? _.slice(savedSheets, 0, 5)
    : savedSheets;

  const data = statusFilter
    ? results?.filter((s) => s.status === statusFilter)
    : results;

  return (
    <>
      {type === 'florence' ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          {savedSheetsIsLoading && (
            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                position: 'absolute',
                paddingTop: '48px',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                zIndex: 99,
              }}
            >
              <CircularProgress color='primary' size='32px' />
            </Box>
          )}

          {data?.map((result) => (
            <SavedSheetCell
              type={type}
              key={result.sheetId}
              savedSheet={result}
              deleteSavedSheet={openDeleteSheetContainer}
              renameSavedSheet={openRenameSheetContainer}
            />
          ))}
          {!shouldReturnFewer() && (
            <PaginationControl
              sx={{ alignSelf: 'center' }}
              count={Math.ceil((savedSheetsResponse?.total ?? 0) / 10)}
              onChange={(e, p) => changePage(p)}
              page={sheetPage}
            />
          )}
        </Box>
      ) : (
        <>
          {adminSearchResults?.map((result) => (
            <SearchResultCell searchResult={result} key={result.id} />
          ))}
        </>
      )}
    </>
  );
};

export default SavedSheetsContainer;
